import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { WrenchIcon, ChevronRightIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useUserRole } from '../../context/UserRoleContext';
import { UpdatesFeed } from '../../components/Trove/UpdatesFeed';
import { UpdatePost, UpdatePostType } from '../../components/Trove/UpdatesPostCard';
import { TroveLayout } from '../../components/Trove/TroveLayout';
import { FilterSection } from '../../components/UI/FilterSidebar';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useDarkClasses } from '../../hooks/useDarkClasses';
// Mock Data for Service Listings
const mockListings: UpdatePost[] = [{
  id: 1,
  type: 'Service Launch',
  title: 'Pre-made Fantasy Cover: "Dragon\'s Breath"',
  description: 'High-resolution fantasy book cover featuring a dragon and warrior. Includes ebook and print wrap.',
  author: 'Dragonfly Designs',
  date: 'Oct 20, 2023',
  serviceCategory: 'Cover Design',
  priceRange: '$75',
  availability: 'Instant Download',
  isNew: true
}, {
  id: 2,
  type: 'Sale',
  title: 'Fantasy Cover Bundle - 3 Covers',
  description: 'Get three matching pre-made covers for your trilogy at a discounted bundle price.',
  author: 'Dragonfly Designs',
  date: 'Oct 19, 2023',
  serviceCategory: 'Cover Design',
  originalPrice: 225,
  salePrice: 180,
  saleEndsAt: '2023-11-30',
  isNew: true
}, {
  id: 3,
  type: 'Vendor Availability',
  title: 'Developmental Editor - Open for Bookings',
  description: 'Specializing in romance and fantasy. 3 slots available for November/December starts.',
  author: 'Sarah Jenkins Editing',
  date: 'Oct 18, 2023',
  serviceCategory: 'Editing',
  slots: 3,
  turnaround: '4 weeks',
  priceRange: '$0.02/word'
}, {
  id: 4,
  type: 'Service Launch',
  title: 'Print + Ebook Formatting Package',
  description: 'Complete formatting solution for your manuscript. Includes custom chapter headers and scene breaks.',
  author: 'FormatPro',
  date: 'Oct 17, 2023',
  serviceCategory: 'Formatting & Production',
  priceRange: '$199',
  turnaround: '5 days'
}, {
  id: 5,
  type: 'Sale',
  title: '50% Off Copy Editing',
  description: 'Limited time offer for new clients! Get 50% off your first 10k words of copy editing.',
  author: 'Precision Edits',
  date: 'Oct 16, 2023',
  serviceCategory: 'Editing',
  saleEndsAt: '2023-10-31'
}, {
  id: 6,
  type: 'Vendor Availability',
  title: 'Contemporary Romance Cover Designer',
  description: "Booking now for Q1 2024. Let's create a cover that hits the tropes and sells your book.",
  author: 'Romance Covers by Jen',
  date: 'Oct 15, 2023',
  serviceCategory: 'Cover Design',
  availability: 'Q1 2024',
  priceRange: '$300+'
}, {
  id: 7,
  type: 'Service Launch',
  title: 'ARC Reader Management Service',
  description: 'Let us handle your ARC team! We manage distribution, follow-ups, and review tracking.',
  author: 'Book Buzz Team',
  date: 'Oct 14, 2023',
  serviceCategory: 'Marketing',
  priceRange: '$150/month'
}, {
  id: 8,
  type: 'Sale',
  title: 'Marketing Bundle: Newsletter + Social',
  description: 'Kickstart your book launch with our comprehensive marketing package. Includes newsletter setup and 1 month of social posts.',
  author: 'Author Marketing Solutions',
  date: 'Oct 13, 2023',
  serviceCategory: 'Marketing',
  originalPrice: 450,
  salePrice: 350
}, {
  id: 9,
  type: 'Vendor Availability',
  title: 'Audiobook Narrator - Fantasy/Sci-Fi',
  description: 'Experienced narrator with home studio. Ready to bring your worlds to life. Listen to samples on my profile.',
  author: 'Voice of Worlds',
  date: 'Oct 12, 2023',
  serviceCategory: 'Audio & Narration',
  priceRange: '$250 PFH',
  availability: 'Immediate Start'
}, {
  id: 10,
  type: 'Sale',
  title: '20% Off Proofreading Services',
  description: 'Polishing your manuscript? Get 20% off proofreading services booked before the end of November.',
  author: 'The Final Polish',
  date: 'Oct 11, 2023',
  serviceCategory: 'Editing',
  saleEndsAt: '2023-11-30'
}];
const ServicesListings = () => {
  const {
    darkMode
  } = useDarkMode();
  const rt = useRoleTheme();
  const dk = useDarkClasses();
  // State for filters
  const [activeTypeFilter, setActiveTypeFilter] = useState<string>('All');
  const [activeServiceFilter, setActiveServiceFilter] = useState<string>('All');
  const [activePriceFilter, setActivePriceFilter] = useState<string>('All');
  // Filter sections
  const filterSections: FilterSection[] = [{
    id: 'type',
    title: 'Listing Type',
    type: 'radio',
    value: activeTypeFilter,
    onChange: val => setActiveTypeFilter(val),
    options: [{
      value: 'All',
      label: 'All Listings'
    }, {
      value: 'Service Launch',
      label: 'Pre-made Products'
    }, {
      value: 'Vendor Availability',
      label: 'Availability'
    }, {
      value: 'Sale',
      label: 'Sales & Discounts'
    }, {
      value: 'Bundle',
      label: 'Bundle Deals'
    } // Note: Logic might need adjustment if 'Bundle' isn't a direct type match in mock data, but 'Sale' covers it or we can filter by title/desc
    ]
  }, {
    id: 'service',
    title: 'Service Category',
    type: 'radio',
    value: activeServiceFilter,
    onChange: val => setActiveServiceFilter(val),
    options: [{
      value: 'All',
      label: 'All Categories'
    }, {
      value: 'Cover Design',
      label: 'Cover Design'
    }, {
      value: 'Editing',
      label: 'Editing'
    }, {
      value: 'Formatting & Production',
      label: 'Formatting & Production'
    }, {
      value: 'Audio & Narration',
      label: 'Audio & Narration'
    }, {
      value: 'Marketing',
      label: 'Marketing'
    }, {
      value: 'Reader Services',
      label: 'Reader Services'
    }]
  }, {
    id: 'price',
    title: 'Price Range',
    type: 'radio',
    value: activePriceFilter,
    onChange: val => setActivePriceFilter(val),
    options: [{
      value: 'All',
      label: 'All Prices'
    }, {
      value: 'Under $100',
      label: 'Under $100'
    }, {
      value: '$100–$300',
      label: '$100–$300'
    }, {
      value: '$300–$600',
      label: '$300–$600'
    }, {
      value: '$600+',
      label: '$600+'
    }]
  }];
  const handleReset = () => {
    setActiveTypeFilter('All');
    setActiveServiceFilter('All');
    setActivePriceFilter('All');
  };
  // Filter logic (basic implementation for mock data)
  const filteredPosts = mockListings.filter(post => {
    const matchesType = activeTypeFilter === 'All' || (activeTypeFilter === 'Bundle' ? post.title.toLowerCase().includes('bundle') : post.type === activeTypeFilter);
    const matchesService = activeServiceFilter === 'All' || post.serviceCategory?.includes(activeServiceFilter);
    // Price filtering would require parsing the price string, skipping for simple mock
    const matchesPrice = activePriceFilter === 'All' || true;
    return matchesType && matchesService && matchesPrice;
  });
  return <TroveLayout filterSections={filterSections} onResetFilters={handleReset} sidebarProps={{
    showServiceCategories: true
  }} data-id="element-16615" data-mp="25p8rcx7nq4km">
      {/* Header */}
      <div className="mb-8" data-id="element-16616" data-mp="1d51zqiwt3suy">
        <div className="flex items-center gap-2 mb-2" data-id="element-16617" data-mp="6jj2rw30vccq">
          <WrenchIcon className={`h-5 w-5 ${rt.textPrimary}`} data-id="element-16618" data-mp="6k2id6579onl" />
          <Link to="/trove" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-16619" data-mp="1kzgtvofi0kd">
            The Trove
          </Link>
          <ChevronRightIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-16620" data-mp="fla82ztnhh62" />
          <Link to="/trove/services" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-16621" data-mp="bivpmgtw8jfb">
            Services Hub
          </Link>
          <ChevronRightIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-16622" data-mp="pgbs5h92jt1s" />
          <span className={`text-sm ${dk.textMuted}`} data-id="element-16623" data-mp="kiir12mmhp8d">All Listings</span>
        </div>
        <h1 className={`text-3xl sm:text-4xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16624" data-mp="z76gqs9sf4qe">
          Service Listings & Marketplace
        </h1>
        <p className={`text-base max-w-2xl ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16625" data-mp="188czz93tmtnc">
          Browse pre-made products, open bookings, sales, and bundle deals from
          service providers.
        </p>
      </div>

      {/* Feed */}
      <UpdatesFeed posts={filteredPosts} title="Latest Listings" showFilters={false} // We use sidebar filters
    maxPosts={20}
    // Pass dummy setters as we handle filtering in parent for this custom page
    activeTypeFilter="All" setActiveTypeFilter={() => {}} activeServiceFilter="All" setActiveServiceFilter={() => {}} activeGenreFilter="All" setActiveGenreFilter={() => {}} data-id="element-16626" data-mp="1mtil5pvz95ul" />
    </TroveLayout>;
};
export default ServicesListings;